import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);
dayjs.locale('ko');

/**
 * 이벤트 타임스탬프를 한국어 형식으로 포맷팅
 * 형식: "YYYY. MM. DD. 오후/오전 HH:mm"
 *
 * @param date Date 객체 또는 ISO 8601 문자열
 * @returns 포맷팅된 날짜 문자열
 */
export function formatEventTimestamp(date: Date | string): string {
  if (!date) return '-';

  const dateObj = dayjs(date);
  if (!dateObj.isValid()) return String(date);

  // "YYYY. MM. DD. 오후/오전 HH:mm" 형식
  return dateObj.format('YYYY. MM. DD. A hh:mm');
}

/**
 * 두 시점 사이의 경과 시간을 포맷팅 (공통 비즈니스 룰)
 * - 5분 미만: "N일 N분 N초" (일, 분이 0이면 생략)
 * - 5분 이상: "약 N일 N분" (초 단위 생략, 일, 분이 0이면 생략)
 *
 * @param fromDate 시작 시점 (Date 객체 또는 ISO 8601 문자열)
 * @param toDate 종료 시점 (Date 객체 또는 ISO 8601 문자열, 기본값: 현재 시간)
 * @returns 포맷팅된 경과 시간 문자열 (접두사 없이)
 */
function formatDuration(fromDate: Date | string, toDate?: Date | string): string {
  if (!fromDate) return '';

  const from = dayjs(fromDate);
  const to = toDate ? dayjs(toDate) : dayjs();

  if (!from.isValid() || !to.isValid()) return '';

  const diff = to.diff(from);
  const dur = dayjs.duration(diff);

  const totalMinutes = Math.floor(dur.asMinutes());
  const days = Math.floor(dur.asDays());
  const minutes = dur.minutes();
  const seconds = dur.seconds();

  const parts: string[] = [];
  const isApproximate = totalMinutes >= 5; // 5분 이상이면 "약" 추가 및 초 생략

  if (days > 0) {
    parts.push(`${days}일`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}분`);
  }
  // 5분 미만일 때만 초 표시
  if (!isApproximate && (seconds > 0 || parts.length === 0)) {
    parts.push(`${seconds}초`);
  }

  if (parts.length === 0) return '';

  const prefix = isApproximate ? '약 ' : '';
  return `${prefix}${parts.join(' ')}`;
}

/**
 * 두 시점 사이의 경과 시간을 포맷팅
 * 형식: "전환까지 N일 N분 N초" 또는 "전환까지 약 N일 N분" (5분 이상)
 *
 * @param fromDate 시작 시점 (Date 객체 또는 ISO 8601 문자열)
 * @param toDate 종료 시점 (Date 객체 또는 ISO 8601 문자열, 기본값: 현재 시간)
 * @returns 포맷팅된 경과 시간 문자열
 */
export function formatElapsedTime(fromDate: Date | string, toDate?: Date | string): string {
  const duration = formatDuration(fromDate, toDate);
  return duration ? `전환까지 ${duration}` : '';
}

/**
 * 두 시점 사이의 경과 시간을 포맷팅 (세션 대기시간용)
 * 형식: "세션 대기시간 N일 N분 N초" 또는 "세션 대기시간 약 N일 N분" (5분 이상)
 *
 * @param fromDate 시작 시점 (Date 객체 또는 ISO 8601 문자열)
 * @param toDate 종료 시점 (Date 객체 또는 ISO 8601 문자열, 기본값: 현재 시간)
 * @returns 포맷팅된 경과 시간 문자열
 */
export function formatWaitingTime(fromDate: Date | string, toDate?: Date | string): string {
  const duration = formatDuration(fromDate, toDate);
  return duration ? `세션 대기시간 ${duration}` : '';
}
